import { useChatsStore } from "@/store/useChatsStore";
import { Asset, AttachmentEntryDto, EntryDtoType } from "@saysimple-platform/dtos";
import { computed, Ref, ref } from "@vue/composition-api";
import { storeToRefs } from "pinia";

// Global to save the state over multiple composable initializations.
const showImageGalleryModal: Ref<boolean> = ref(false);
const imageGallerySlideIndex: Ref<number> = ref(0);

// This composable handles the combination of the gallery and modal.
export default function useImageGalleryModal() {
    const { activeChat } = storeToRefs(useChatsStore());

    // Note that attachment entries differs from assets (images) in the way that one entry can have multiple assets.
    const attachmentEntries = computed((): AttachmentEntryDto[] => {
        return activeChat.value.entries
            .filter((entry: EntryDtoType) => entry.type === "attachment")
            .flat() as AttachmentEntryDto[];
    });

    // Note at the moment we only support images.
    const images = computed((): Array<Asset & { entryId: number }> => {
        // Looks a bit difficult, but we'll also have to deal with single entry with multiple assets, so
        // for each asset, we like to concat the entry id, so we know the ENTRY info when we open for example the 2nd image of one entry.
        return attachmentEntries.value
            .flatMap((entry) => entry.assets.map((asset) => ({ ...asset, entryId: entry.id })))
            .filter((asset) => asset.category === "image");
    });

    const hasImages = computed((): boolean => attachmentEntries.value.length > 0);

    // You can pass an asset id to directly show that particular asset when the modal opens.
    // The index of the carousel slides correspondents with the indexes of the images array.
    const openImageGalleryModal = (assetId?: number): void => {
        let slideIndex = images.value.findIndex((asset: Asset) => asset.id === assetId);

        if (slideIndex === -1) {
            slideIndex = 0;
        }

        showImageGalleryModal.value = true;
        imageGallerySlideIndex.value = slideIndex;
    };

    return {
        attachmentEntries,
        hasImages,
        imageGallerySlideIndex,
        images,
        openImageGalleryModal,
        showImageGalleryModal,
    };
}
